import { useState, useEffect, useCallback } from "react";
import { Plan } from "types";

interface UseGetAvailablePlansReturn {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const useGetAvailablePlans = (): UseGetAvailablePlansReturn => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          permissionKey: "get_available_subscription_plans",
        },
        body: JSON.stringify({
          query: `
            query GetAvailableSubscriptionPlans {
              getAvailableSubscriptionPlans {
                uuid
                name
                slug
                description
                price_per_seat
                price_per_seat_annual
                seat_limit
                practice_limit
                group_limit
                is_default
                stripe_product_id
                stripe_price_id
                stripe_annual_price_id
                features
                patientLimit
                assignmentLimit
                customAssignmentLimit
                customResourceLimit
                fileLimit
                groupLimit
              }
            }
          `,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch plans");
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      setPlans(data.data.getAvailableSubscriptionPlans);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    plans,
    loading,
    error,
    refetch: fetchPlans,
  };
};

export default useGetAvailablePlans;
